import React, { useEffect, useState } from 'react';
import { ReactComponent as XlsUploadIcon } from 'assets/icons/brand-details.svg';
import { ReactComponent as XlsReviewIcon } from 'assets/icons/meals.svg';
import { Outlet, useLocation } from 'react-router-dom';
import useDashboardContext from 'contexts/dashboard.context';
import { ROUTE_KEYS, ROUTE_PREFIX } from 'utils/constants';
import RightSidebar from 'components/right-sidebar';
import BottomNavigation from 'components/bottom-navigation';
import useNewMealXlsContext from 'contexts/new-meal-xls.context';
import { apiService } from 'services/base.service';
import { SuccessModal } from 'components/base/success-modal';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import { XlsAddonsType, XlsMealAddonsType } from 'utils/types';
import { checkImageUrlValidity, getTrimmedPath, isFCShopCategory } from 'utils/functions';
import { ImageDataType } from 'contexts/new-meal-xls.context/context';
import { getBaseAddonCategory, getBaseMeals } from 'pages/base-items/util';

const NewMealXlsContent = () => {
  const { setRightSection } = useDashboardContext();
  const {
    mappedMeals,
    imageData,
    setMappedMeals,
    meals,
    selectedBrand,
    setBaseAddonCategories,
    setBaseMeals
  } = useNewMealXlsContext();
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const isFcShop = isFCShopCategory();

  useEffect(() => {
    fetchBaseAddonCategories();
    fetchBaseMeals();
  }, []);
  const fetchBaseAddonCategories = async () => {
    const categories = await getBaseAddonCategory(true);
    setBaseAddonCategories(categories);
  };
  const fetchBaseMeals = async () => {
    await getBaseMeals(setBaseMeals, true);
  };

  const MEAL_NAV_LIST = [
    {
      title: 'Upload Files',
      link: `${ROUTE_KEYS.NEW_MEAL_XLS}/${ROUTE_KEYS.NEW_MEAL_XLS_UPLOAD}`,
      Icon: XlsUploadIcon
    },
    {
      title: 'Review Items',
      link: `${ROUTE_KEYS.NEW_MEAL_XLS}/${
        !isFcShop ? ROUTE_KEYS.NEW_MEAL_XLS_REVIEW : ROUTE_KEYS.NEW_SHOP_MEAL_XLS_REVIEW
      }`,
      Icon: XlsReviewIcon
    }
  ];

  const createMeals = () => {
    const newMealArray: any = [];
    meals.map((meal: any) => {
      meal.images = meal.image
        ? [meal.image]
        : imageData
            ?.filter((image: ImageDataType) => image?.name?.trim() === meal['Image']?.trim())
            .map((image) => image.url);

      newMealArray.push({
        ...meal,
        brand: { name: selectedBrand?.text, id: selectedBrand?.value }
      });
    });
    setMappedMeals(newMealArray);
  };

  const { pathname } = useLocation();

  const onNextClick = () => {
    if (selectedBrand && meals && pathname.includes('upload')) createMeals();
  };

  const getPayload = () => {
    const data = mappedMeals.map((meal: any) => {
      const addon_data = meal?.addons?.map((addonCat: XlsAddonsType) => {
        return {
          base_addon_category_id: addonCat.id,
          addons_data: addonCat?.addons?.map((addon: XlsMealAddonsType) => {
            return {
              addon_meal_id: addon.addon_meal_id,
              name: addon.name,
              default: addon?.default,
              quantity: String(addon?.quantity)
            };
          })
        };
      });
      return {
        budget_combo: meal?.budget_combo,
        base_meal_id: meal?.base_meal_id,
        meal_category_name: meal?.meal_category?.name,
        meal_category_horizontal: meal?.meal_category_horizontal,
        preparation_time: meal?.preparation_time ?? '',
        name: meal.name,
        description: meal?.description,
        images: checkImageUrlValidity(meal?.images) || [],
        item_type: 'FOOD',
        addons: addon_data ?? [],
        quantity: String(meal?.quantity)
      };
    });

    return {
      brand_id: selectedBrand?.value,
      meals: [...data]
    };
  };

  const addMeal = async () => {
    setLoading(true);

    const payload = getPayload();
    try {
      if (!isFcShop) {
        await apiService.post('meal/csv', { data: payload });
      } else {
        await apiService.post('shops/products', {
          data: {
            shop_id: selectedBrand?.value,
            products: meals.map((meal) => ({
              name: meal.name,
              description: meal.description,
              price: meal.price,
              category_name: meal.category,
              weight: meal.weight,
              size: meal.size,
              images: [meal.imageUrl]
            }))
          }
        });
      }
      setShowSuccess(true);
    } catch (err: any) {
      console.log(err);
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setRightSection(<RightSidebar list={MEAL_NAV_LIST} />);

    return () => {
      setRightSection(null);
    };
  }, []);

  const isDisabled = () => {
    if (`/${getTrimmedPath()}` === MEAL_NAV_LIST[0].link)
      return !selectedBrand || meals.length == 0;
  };

  return (
    <div>
      <Outlet />
      <BottomNavigation
        list={MEAL_NAV_LIST}
        onConfirm={addMeal}
        loading={loading}
        disabled={isFcShop ? meals.length < 1 : isDisabled()}
        onNextClick={onNextClick}
      />
      <SuccessModal
        onClose={() => setShowSuccess(false)}
        show={showSuccess}
        link={`${ROUTE_PREFIX.FOOD}/${ROUTE_KEYS.MEALS}`}
        linkText='Back to meals'>
        {isFcShop ? 'Shop Items' : 'Meals'} Uploaded Successfully
      </SuccessModal>
    </div>
  );
};

export default NewMealXlsContent;
